/**
 * Всплывающая панель под триггером (селекты, панель упоминаний): позиция в fixed-координатах,
 * закрытие по клику снаружи и Esc. Прокрутка контейнера с кнопкой закрывает панель, остальная — нет.
 */
import { nextTick, onBeforeUnmount, ref, type Ref } from 'vue';

export function usePopover(trigger: Ref<HTMLElement | null>, opts: { width?: () => number; align?: 'left' | 'right'; maxHeight?: number } = {}) {
  const open = ref(false);
  const panel = ref<HTMLElement | null>(null);
  const pos = ref({ top: 0, left: 0, width: 0, maxHeight: opts.maxHeight ?? 320 });

  function place() {
    const r = trigger.value?.getBoundingClientRect();
    if (!r) return;
    const width = Math.max(opts.width?.() ?? r.width, 160);
    let left = opts.align === 'right' ? r.right - width : r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const wanted = Math.min(opts.maxHeight ?? 320, panel.value?.scrollHeight ?? opts.maxHeight ?? 320);
    // места снизу мало, а сверху больше — открываемся вверх
    if (below < wanted && above > below) {
      const maxHeight = Math.min(opts.maxHeight ?? 320, above);
      pos.value = { top: r.top - 6 - Math.min(wanted, maxHeight), left, width, maxHeight };
    } else {
      pos.value = { top: r.bottom + 6, left, width, maxHeight: Math.min(opts.maxHeight ?? 320, Math.max(below, 120)) };
    }
  }

  function onOutside(e: MouseEvent) {
    const t = e.target as Node;
    if (trigger.value?.contains(t) || panel.value?.contains(t)) return;
    hide();
  }
  // закрываемся, только если прокрутилось то, в чём лежит сама кнопка (иначе панель «оторвётся»);
  // прокрутка внутри панели или соседних блоков (колонки доски обновляются сами) — не повод
  function onScroll(e: Event) {
    const t = e.target;
    if (t === document || t === document.documentElement || (t instanceof Node && trigger.value && t.contains(trigger.value))) hide();
  }
  const onResize = () => hide();
  const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && hide();

  async function show() {
    if (open.value) return;
    open.value = true;
    await nextTick();
    place();
    await nextTick();
    place(); // второй проход — уже с реальной высотой панели
    window.addEventListener('mousedown', onOutside, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onEsc);
  }
  function hide() {
    if (!open.value) return;
    open.value = false;
    window.removeEventListener('mousedown', onOutside, true);
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('keydown', onEsc);
  }
  const toggle = () => (open.value ? hide() : void show());

  onBeforeUnmount(hide);
  return { open, panel, pos, show, hide, toggle, place };
}
